import React, { useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { Box, Typography, Divider } from "@material-ui/core";
import AppInput from "components/Input/AppInput";
import CustomDatePicker from "components/Input/CustomDatePicker";
import AppButton from "components/CustomButtons/AppButton";
import SelectField from "components/Select/SelectField";
import AppColor from "utilities/AppColor";
import { useDispatch } from "react-redux";
import { submitIntakeLinkSupportNeeds } from "store/slices/client";
import { showToast } from "store/slices/toast";
import { useTranslation } from "react-i18next";
import moment from "moment";

const IntakeLinkSupportNeeds = ({ clientData, token, onNextStep, updateClientData }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    serviceType: clientData?.serviceType || "",
    carePlanPersonalCare: clientData?.carePlanResponse?.personalCare || "",
    carePlanMobility: clientData?.carePlanResponse?.mobility || "",
    carePlanMedicationManagement: clientData?.carePlanResponse?.medicationManagement || "",
    carePlanCommunityAccess: clientData?.carePlanResponse?.communityAccess || "",
    carePlanCommunicationStrategy: clientData?.carePlanResponse?.communicationStrategy || "",
    carePlanGoals: clientData?.carePlanResponse?.goals || "",
    carePlanPrimaryDiagnosis: clientData?.carePlanResponse?.primaryDiagnosis || "",
    carePlanSecondaryConditions: clientData?.carePlanResponse?.secondaryConditions || "",
    location: clientData?.location || "",
    locationExpired: clientData?.locationExpired || null,
  };

  const validationSchema = Yup.object().shape({
    serviceType: Yup.string().required(t("validation.required")),
    carePlanCommunicationStrategy: Yup.string().max(255, "validation.max_length_255"),
    carePlanPrimaryDiagnosis: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    carePlanSecondaryConditions: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    carePlanPersonalCare: Yup.string().required(t("validation.required")),
    carePlanMobility: Yup.string().required(t("validation.required")),
    carePlanMedicationManagement: Yup.string().required(t("validation.required")),
    carePlanCommunityAccess: Yup.string().required(t("validation.required")),

    location: Yup.string().required(t("validation.required")).max(255, "validation.max_length_255"),
    locationExpired: Yup.date()
      .nullable()
      .when("serviceType", (serviceType, schema) => {
        return serviceType === "MTA"
          ? schema.required(t("validation.required"))
          : schema.nullable().notRequired();
      }),
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    let formattedLocationExpired = null;
    if (values.serviceType === "MTA" && values.locationExpired) {
      formattedLocationExpired = moment(values.locationExpired).isValid()
        ? moment(values.locationExpired).format("YYYY/MM/DD")
        : values.locationExpired;
    }

    const payload = {
      intakeLinkToken: token,
      serviceType: values.serviceType,
      carePlanGoals: values.carePlanGoals,
      carePlanPersonalCare: values.carePlanPersonalCare,
      carePlanMobility: values.carePlanMobility,
      carePlanMedicationManagement: values.carePlanMedicationManagement,
      carePlanCommunityAccess: values.carePlanCommunityAccess,
      carePlanCommunicationStrategy: values.carePlanCommunicationStrategy,
      carePlanPrimaryDiagnosis: values.carePlanPrimaryDiagnosis,
      carePlanSecondaryConditions: values.carePlanSecondaryConditions,
      location: values.location,
      locationExpired: formattedLocationExpired,
    };

    try {
      const response = await dispatch(submitIntakeLinkSupportNeeds(payload));
      if (response && response.payload) {
        if (updateClientData) {
          updateClientData({ serviceType: values.serviceType });
        }
        onNextStep(true);
      }
    } catch (e) {
      console.log(e);
      dispatch(showToast({ message: "Failed to submit Support Needs", type: "error" }));
    }
    setLoading(false);
  };
  const personalCareOptions = ["FULL_ASSIST", "PROMPTING_PHYSICAL_ASSIST", "PROMPTING_ONLY", "SUPERVISED_ONLY", "INDEPENDENT"].map((v) => ({
    value: v, label: t(`personal_care.${v}`),
  }));
  const mobilityOptions = ["AMBULANT_NO_AIDS", "WALKING_FRAME", "MANUAL_WHEELCHAIR", "POWERED_WHEELCHAIR", "HOIST_REQUIRED", "AMBULANT_SUPERVISION"].map((v) => ({
    value: v, label: t(`mobility.${v}`),
  }));
  const medicationManagementOptions = ["WORKER_ADMINISTERS", "SUPERVISED_SELF_ADMIN", "INDEPENDENT", "PRN_ONLY"].map((v) => ({
    value: v, label: t(`medication_management.${v}`),
  }));
  const communityAccessOptions = ["RATIO_1_1_ALL_TIMES", "RATIO_1_1_FAMILIAR_ONLY", "RATIO_1_2", "SUPERVISED_GROUP", "INDEPENDENT_FAMILIAR"].map((v) => ({
    value: v, label: t(`community_access.${v}`),
  }));
  const serviceTypeOptions = [
    { value: "SIL", label: t(`service_type.SIL`) },
    { value: "MTA", label: t(`service_type.MTA`) },
    { value: "COMMUNITY_ACCESS", label: t(`service_type.COMMUNITY_ACCESS`) },
    { value: "IN_HOME_SUPPORT", label: t(`service_type.IN_HOME_SUPPORT`) }
  ];
  
  const fullName = clientData ? `${clientData.firstName || ""} ${clientData.lastName || ""}`.trim() : null;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, values }) => {
        return (
        <Form>
          <Typography variant="h6" style={{ color: AppColor.mainColor, marginBottom: 15 }}>
            {t("label.support_needs")}
          </Typography>
          <Divider style={{ marginBottom: 20 }} />
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <Field
                component={SelectField}
                label={t("label.service_type")}
                name="serviceType"
                required
                options={serviceTypeOptions}
              />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={SelectField} name="carePlanPersonalCare" label="label.personal_care" options={personalCareOptions} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={SelectField} name="carePlanMedicationManagement" label="label.medication_management" options={medicationManagementOptions} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={SelectField} name="carePlanCommunityAccess" label="label.community_access" options={communityAccessOptions} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={SelectField} name="carePlanMobility" label="label.mobility" options={mobilityOptions} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="carePlanCommunicationStrategy" label="label.communication_strategy" />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="carePlanPrimaryDiagnosis" label={t("label.primary_diagnosis")} multiline rows={3} required />
            </div>
            <div className="col-sm-12 col-md-6">
              <Field component={AppInput} name="carePlanSecondaryConditions" label={t("label.secondary_conditions")} multiline rows={3} required />
            </div>
            {
              fullName &&
              <div className="col-sm-12 col-md-12">
                <Field component={AppInput} type="textarea" name="carePlanGoals" label={t("label.carePlanGoals", { value: fullName })} />
              </div>
            }
            
            <div className="col-sm-12 col-md-12">
              <Typography variant="subtitle2" style={{ marginTop: 10, marginBottom: 10, fontWeight: "bold" }}>{t("label.location_and_housing")}</Typography>
            </div>
            <div className="col-sm-12 col-md-12">
              <Field component={AppInput} name="location" label={t(`locationLabel.${values.serviceType || "SIL"}`)} required />
            </div>
            {values.serviceType === "MTA" && (
              <div className="col-sm-12 col-md-12">
                <Field
                  label={t("label.location_expired_date")}
                  component={CustomDatePicker}
                  name="locationExpired"
                  placeholder="dd/mm/yyyy"
                  formatDateJob={true}
                  classInput="date-picker-container-custom"
                  custom={{
                      format: 'MM/DD/yyyy',
                  }}
                  required
                />
              </div>
            )}
          </div>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <AppButton
              label={t("button.saveAndNext")}
              type="submit"
              buttonColor={AppColor.mainColor}
              isLoading={loading}
              disabled={loading}
              style={{ width: "150px" }}
            />
          </Box>
        </Form>
      )}}
    </Formik>
  );
};

export default IntakeLinkSupportNeeds;
